import React from 'react';
import { Container, Typography, Box, Paper, Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SiteFooter } from '../components/SiteFooter';
import { BrandLogo } from '../components/BrandLogo';

/*
  A fixed date, changed by hand whenever the wording changes. It used to be `new Date()`, which
  claimed the terms had been updated on whatever day you happened to read them.
*/
const LAST_UPDATED = 'October 16, 2026';

export const Terms: React.FC = () => {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 4 } }}>
        {/* Matches the About page: logo, then the heading. */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <BrandLogo maxWidth={260} height={56} />
        </Box>

        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          Last Updated: {LAST_UPDATED}
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography paragraph>
            ShoutVector ("the Service") is provided by REMAGINE LLC ("we", "us"). By accessing and using the Service, you accept and agree to be bound by these
            Terms of Service. If you do not agree to them, please do not use the Service.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            2. Description of Service
          </Typography>
          <Typography paragraph>
            ShoutVector is an internal social media publishing tool. It lets REMAGINE LLC and its affiliated companies
            create, schedule, and publish content to the LinkedIn, Facebook, Instagram, and X accounts and Pages they own,
            and see how that content performs. Access is by invitation only: there is no public sign-up, and accounts are
            created by us for our staff and for reviewers we authorize.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            3. User Accounts
          </Typography>
          <Typography paragraph>
            You are responsible for:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
            <li>Ensuring all information provided is accurate and up-to-date</li>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            4. Acceptable Use
          </Typography>
          <Typography paragraph>
            You agree not to use the Service to:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>Violate any laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Post spam, malicious content, or misleading information</li>
            <li>Harass, abuse, or harm others</li>
            <li>Attempt to gain unauthorized access to the Service or related systems</li>
            <li>Interfere with or disrupt the Service</li>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            5. Social Media Platform Integration
          </Typography>
          <Typography paragraph>
            When you connect your social media accounts to ShoutVector, you:
          </Typography>
          <Typography component="ul" sx={{ pl: 4 }}>
            <li>Grant us permission to post on your behalf according to your instructions</li>
            <li>Acknowledge that you are responsible for compliance with each platform's terms of service</li>
            <li>Understand that we are not responsible for changes to third-party platform APIs or policies</li>
            <li>Can revoke access at any time through platform settings or ShoutVector</li>
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            6. Content Ownership and Rights
          </Typography>
          <Typography paragraph>
            You retain all rights to the content you create and post through ShoutVector. By using the Service, you grant
            us a limited license to store, process, and transmit your content solely for the purpose of providing the Service.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            7. Service Availability
          </Typography>
          <Typography paragraph>
            We strive to maintain high availability but do not guarantee uninterrupted access to the Service. We reserve
            the right to modify, suspend, or discontinue any part of the Service at any time with or without notice.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            8. Limitation of Liability
          </Typography>
          <Typography paragraph>
            To the maximum extent permitted by law, REMAGINE LLC shall not be liable for any indirect,
            incidental, special, consequential, or punitive damages resulting from your use or inability to use the Service.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            9. Termination
          </Typography>
          <Typography paragraph>
            We reserve the right to terminate or suspend your account and access to the Service at our sole discretion,
            without notice, for conduct that we believe violates these Terms of Service or is harmful to other users,
            us, or third parties, or for any other reason.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            10. Changes to Terms
          </Typography>
          <Typography paragraph>
            We may update these Terms of Service from time to time. Continued use of the Service after changes constitutes
            acceptance of the updated terms.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            11. Governing Law
          </Typography>
          <Typography paragraph>
            These Terms are governed by the laws of the Commonwealth of Massachusetts, United States, without regard to its
            conflict of law provisions.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            12. Contact Information
          </Typography>
          <Typography paragraph>
            If you have any questions about these Terms of Service, please contact us at:
          </Typography>
          <Typography paragraph>
            <strong>REMAGINE LLC</strong><br />
            {/*
              A MUI Link rather than a bare <a> so it takes the theme's primary colour — the
              same colour the Back to Home button below uses. Matching by construction rather
              than by a copied hex, so a theme change moves both together.
            */}
            <Link
              href="https://www.remagine.com/contact"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Contact Support
            </Link>
          </Typography>
        </Box>

        {/*
          Points at "/" rather than "/login": signed out that redirects to the sign-in screen
          anyway, and signed in it lands on the Dashboard. Same as the About page.
        */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button component={RouterLink} to="/">
            Back to Home
          </Button>
        </Box>
      </Paper>
      <SiteFooter variant="standalone" />
    </Container>
  );
};
